import React, { useState, useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useSearchParams } from 'react-router-dom';
import { DashboardLayout, Card } from '../components';
import { Breadcrumb } from '../components/Breadcrumb';
import { notificationApi } from '../services/api';
import { NotificationDeliveryLog } from '../components/admin/NotificationDeliveryLog';
import { NotificationTemplatePreview } from '../components/admin/NotificationTemplatePreview';

interface NotificationDefinition {
  id: string;
  eventKey: string;
  type: string;
  title: string;
  bodyTemplate: string;
  recipients: string[];
  channels: string[];
  requiredChannels?: string[];
  categoryLocked?: boolean;
  settingsEditable?: boolean;
  unavailableReason?: 'legacy_only' | 'not_implemented' | null;
  trigger: { type: string; event?: string; condition?: string; offsetDays?: number; referenceField?: string };
  priority: string;
  active: boolean;
  metadata: { icon: string; color: string; actionUrl: string };
}

interface NotificationRecord {
  id: string;
  definitionId: string;
  eventKey: string;
  type: string;
  title: string;
  body: string;
  recipientUserId: string;
  recipientType: string;
  channels: string[];
  priority: string;
  read: boolean;
  readAt: string | null;
  metadata: { icon: string; color: string; actionUrl: string };
  createdAt: string;
}

type TabView = 'definitions' | 'log' | 'deliveries' | 'preview';

export function AdminNotifications() {
  const { t } = useTranslation();
  const [searchParams] = useSearchParams();
  const requestedTab = searchParams.get('tab');
  const initialTab: TabView = requestedTab === 'log' || requestedTab === 'deliveries' || requestedTab === 'preview' ? requestedTab : 'definitions';
  const [activeTab, setActiveTab] = useState<TabView>(initialTab);
  const [definitions, setDefinitions] = useState<NotificationDefinition[]>([]);
  const [notifications, setNotifications] = useState<NotificationRecord[]>([]);
  const [isLoading, setIsLoading] = useState(true);

  // Filters
  const [filterType, setFilterType] = useState('');
  const [filterRecipient, setFilterRecipient] = useState('');
  const [filterPriority, setFilterPriority] = useState('');
  const [filterChannel, setFilterChannel] = useState('');
  const [filterActive, setFilterActive] = useState('');
  const [filterSearch, setFilterSearch] = useState('');

  // Log filters
  const [logFilterType, setLogFilterType] = useState('');
  const [logFilterPriority, setLogFilterPriority] = useState('');
  const [logFilterRead, setLogFilterRead] = useState('');
  const [logFilterRecipient, setLogFilterRecipient] = useState('');
  const [logFilterSearch, setLogFilterSearch] = useState('');
  const [selectedNotif, setSelectedNotif] = useState<NotificationRecord | null>(null);

  // Definition editor
  const [editDef, setEditDef] = useState<NotificationDefinition | null>(null);
  const [isCreatingDef, setIsCreatingDef] = useState(false);
  const [defForm, setDefForm] = useState({
    eventKey: '', type: 'Kontrakt', title: '', bodyTemplate: '',
    recipients: [] as string[], channels: ['in_app'] as string[],
    triggerType: 'event', triggerEvent: '', triggerCondition: '', triggerOffsetDays: '',
    priority: 'normal', active: true,
    icon: 'bell', color: 'secondary', actionUrl: ''
  });
  const [isSavingDef, setIsSavingDef] = useState(false);
  const [defMessage, setDefMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null);
  const requiredChannels = editDef?.requiredChannels ?? [];
  // A locked category keeps its e-mail while active, but can be switched off.
  const categoryLocked = editDef?.categoryLocked === true;
  const settingsUnavailable = editDef?.settingsEditable === false;

  useEffect(() => {
    loadData();
  }, []);


  const loadData = async () => {
    setIsLoading(true);
    try {
      const [defs, notifs] = await Promise.all([
        notificationApi.getDefinitions(),
        notificationApi.getMyNotifications()
      ]);
      setDefinitions(defs);
      setNotifications(notifs);
    } catch (error) {
      console.error('Failed to load notification data:', error);
    } finally {
      setIsLoading(false);
    }
  };

  // Unique values for filter dropdowns
  const types = useMemo(() => [...new Set(definitions.map(d => d.type))].sort(), [definitions]);
  const recipients = useMemo(() => [...new Set(definitions.flatMap(d => d.recipients))].sort(), [definitions]);
  const priorities = ['high', 'normal', 'low'];
  const channels = ['in_app', 'email', 'sms'];

  // Filtered definitions
  const filteredDefinitions = useMemo(() => {
    return definitions.filter(d => {
      if (filterType && d.type !== filterType) return false;
      if (filterRecipient && !d.recipients.includes(filterRecipient)) return false;
      if (filterPriority && d.priority !== filterPriority) return false;
      if (filterChannel && !d.channels.includes(filterChannel)) return false;
      if (filterActive === 'true' && !d.active) return false;
      if (filterActive === 'false' && d.active) return false;
      if (filterSearch && !d.title.toLowerCase().includes(filterSearch.toLowerCase()) && !d.eventKey.toLowerCase().includes(filterSearch.toLowerCase())) return false;
      return true;
    });
  }, [definitions, filterType, filterRecipient, filterPriority, filterChannel, filterActive, filterSearch]);

  // Filtered log
  const filteredNotifications = useMemo(() => {
    return notifications.filter(n => {
      if (logFilterType && n.type !== logFilterType) return false;
      if (logFilterPriority && n.priority !== logFilterPriority) return false;
      if (logFilterRead === 'true' && !n.read) return false;
      if (logFilterRead === 'false' && n.read) return false;
      if (logFilterRecipient && n.recipientType !== logFilterRecipient) return false;
      if (logFilterSearch && !n.title.toLowerCase().includes(logFilterSearch.toLowerCase()) && !n.body.toLowerCase().includes(logFilterSearch.toLowerCase())) return false;
      return true;
    });
  }, [notifications, logFilterType, logFilterPriority, logFilterRead, logFilterRecipient, logFilterSearch]);

  const getPriorityBadge = (priority: string) => {
    const map: Record<string, string> = { high: 'danger', normal: 'primary', low: 'secondary' };
    return `bg-${map[priority] || 'secondary'}`;
  };

  const getPriorityLabel = (priority: string) => {
    const map: Record<string, string> = { high: t('adminNotifications.priority.high'), normal: t('adminNotifications.priority.normal'), low: t('adminNotifications.priority.low') };
    return map[priority] || priority;
  };

  const getChannelBadge = (channel: string) => {
    const map: Record<string, { color: string; label: string }> = {
      in_app: { color: 'bg-info', label: t('adminNotifications.channelInApp') },
      email: { color: 'bg-primary', label: t('adminNotifications.channelEmail') },
      sms: { color: 'bg-warning text-dark', label: t('adminNotifications.channelSms') }
    };
    return map[channel] || { color: 'bg-secondary', label: channel };
  };

  const getTriggerLabel = (trigger: NotificationDefinition['trigger']) => {
    if (trigger.type === 'event') return `Event: ${trigger.event || ''}`;
    if (trigger.type === 'scheduled') return t('adminNotifications.triggerScheduled', { days: trigger.offsetDays || 0 });
    return trigger.type;
  };

  // Stable logic values (used for filtering/lookup and stored on definitions)
  const allTypes = ['Kontrakt', 'Registrering', 'System', 'Transaktion', 'Etappmål', 'Påminnelse'];
  const allRecipients = ['Kund', 'Företag', 'Admin', 'Jurist', 'Besiktningsman'];
  const allChannels = ['in_app', 'email', 'sms'];
  const allPriorities = ['high', 'normal', 'low'];
  const allColors = ['primary', 'secondary', 'success', 'danger', 'warning', 'info'];

  // Display-only lookups keyed by the stable Swedish logic value
  const typeLabelMap: Record<string, string> = {
    Kontrakt: t('adminNotifications.typeKontrakt'),
    Registrering: t('adminNotifications.typeRegistrering'),
    System: t('adminNotifications.typeSystem'),
    Transaktion: t('adminNotifications.typeTransaktion'),
    'Etappmål': t('adminNotifications.typeEtappmal'),
    'Påminnelse': t('adminNotifications.typePaminnelse')
  };
  const typeLabel = (ty: string) => typeLabelMap[ty] || ty;
  const recipientLabelMap: Record<string, string> = {
    Kund: t('adminNotifications.recipientKund'),
    'Företag': t('adminNotifications.recipientForetag'),
    Admin: t('adminNotifications.recipientAdmin'),
    Jurist: t('adminNotifications.recipientJurist'),
    Besiktningsman: t('adminNotifications.recipientBesiktningsman')
  };
  const recipientLabel = (r: string) => recipientLabelMap[r] || r;

  const openDefEditor = (def: NotificationDefinition | null) => {
    if (def) {
      setEditDef(def);
      setIsCreatingDef(false);
      setDefForm({
        eventKey: def.eventKey, type: def.type, title: def.title, bodyTemplate: def.bodyTemplate,
        recipients: [...def.recipients], channels: [...def.channels],
        triggerType: def.trigger.type, triggerEvent: def.trigger.event || '',
        triggerCondition: def.trigger.condition || '', triggerOffsetDays: def.trigger.offsetDays != null ? String(def.trigger.offsetDays) : '',
        priority: def.priority, active: def.active,
        icon: def.metadata.icon ?? 'bell', color: def.metadata.color ?? 'secondary', actionUrl: def.metadata.actionUrl ?? ''
      });
    } else {
      setEditDef(null);
      setIsCreatingDef(true);
      setDefForm({
        eventKey: '', type: 'Kontrakt', title: '', bodyTemplate: '',
        recipients: [], channels: ['in_app'],
        triggerType: 'event', triggerEvent: '', triggerCondition: '', triggerOffsetDays: '',
        priority: 'normal', active: true,
        icon: 'bell', color: 'secondary', actionUrl: ''
      });
    }
    setDefMessage(null);
  };

  const closeDefEditor = () => { setEditDef(null); setIsCreatingDef(false); setDefMessage(null); };

  const toggleArrayItem = (arr: string[], item: string) =>
    arr.includes(item) ? arr.filter(x => x !== item) : [...arr, item];

  const handleSaveDef = async () => {
    if (settingsUnavailable) return;
    if (!defForm.eventKey.trim() || !defForm.title.trim()) {
      setDefMessage({ type: 'error', text: t('adminNotifications.defEditor.requiredFields') });
      return;
    }
    setIsSavingDef(true);
    setDefMessage(null);
    const payload = {
      eventKey: defForm.eventKey, type: defForm.type, title: defForm.title, bodyTemplate: defForm.bodyTemplate,
      recipients: defForm.recipients, channels: defForm.channels,
      trigger: {
        type: defForm.triggerType,
        ...(defForm.triggerEvent && { event: defForm.triggerEvent }),
        ...(defForm.triggerCondition && { condition: defForm.triggerCondition }),
        ...(defForm.triggerOffsetDays && { offsetDays: parseInt(defForm.triggerOffsetDays) })
      },
      priority: defForm.priority, active: defForm.active,
      metadata: { icon: defForm.icon, color: defForm.color, actionUrl: defForm.actionUrl }
    };
    try {
      if (editDef) {
        await notificationApi.updateDefinition(editDef.id, payload);
      } else {
        await notificationApi.createDefinition(payload);
      }
      await loadData();
      closeDefEditor();
    } catch {
      setDefMessage({ type: 'error', text: t('adminNotifications.defEditor.saveError') });
    } finally {
      setIsSavingDef(false);
    }
  };

  const clearFilters = () => {
    setFilterType(''); setFilterRecipient(''); setFilterPriority('');
    setFilterChannel(''); setFilterActive(''); setFilterSearch('');
  };

  const clearLogFilters = () => {
    setLogFilterType(''); setLogFilterPriority(''); setLogFilterRead(''); setLogFilterRecipient(''); setLogFilterSearch('');
  };

  if (isLoading) {
    return (
      <DashboardLayout>
        <div className="d-flex justify-content-center align-items-center py-5">
          <div className="spinner-border text-primary" role="status">
            <span className="visually-hidden">{t('common.loading')}</span>
          </div>
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <div className="page-header mb-4">
        <div className="page-header-left d-flex align-items-center">
          <div className="page-header-title">
            <h5 className="fw-bold mb-0">{t('adminNotifications.title')}</h5>
          </div>
          <Breadcrumb items={[
            { label: t('nav.home'), path: '/admin/home' },
            { label: t('adminNotifications.title') }
          ]} />
        </div>
        <div className="page-header-right ms-auto d-none d-sm-block">
          <p className="fw-bold text-muted mb-0">{t('adminNotifications.description')}</p>
        </div>
      </div>

      <div className="alert alert-info mb-4" role="note">
        <p className="mb-2">{t('adminNotifications.deliverySettingsHelp')}</p>
        <p className="mb-2">{t('adminNotifications.zignedDeliveryHelp')}</p>
        <p className="mb-0">{t('adminNotifications.smsDeliveryHelp')}</p>
      </div>

      {/* Stats row */}
      <div className="row mb-4">
        <div className="col-md-3 mb-3 mb-md-0">
          <Card>
            <div className="text-center">
              <div className="fs-2 fw-bold text-primary">{definitions.length}</div>
              <small className="text-muted">{t('adminNotifications.stats.totalDefinitions')}</small>
            </div>
          </Card>
        </div>
        <div className="col-md-3 mb-3 mb-md-0">
          <Card>
            <div className="text-center">
              <div className="fs-2 fw-bold text-success">{definitions.filter(d => d.active).length}</div>
              <small className="text-muted">{t('adminNotifications.stats.active')}</small>
            </div>
          </Card>
        </div>
        <div className="col-md-3 mb-3 mb-md-0">
          <Card>
            <div className="text-center">
              <div className="fs-2 fw-bold text-secondary">{definitions.filter(d => !d.active).length}</div>
              <small className="text-muted">{t('adminNotifications.stats.inactive')}</small>
            </div>
          </Card>
        </div>
        <div className="col-md-3">
          <Card>
            <div className="text-center">
              <div className="fs-2 fw-bold text-info">{notifications.length}</div>
              <small className="text-muted">{t('adminNotifications.stats.totalSent')}</small>
            </div>
          </Card>
        </div>
      </div>

      {/* Tabs */}
      <ul className="nav nav-tabs mb-4">
        <li className="nav-item">
          <button className={`nav-link ${activeTab === 'definitions' ? 'active' : ''}`} onClick={() => setActiveTab('definitions')}>
            <i className="feather-list me-2"></i>{t('adminNotifications.tabs.definitions')}
          </button>
        </li>
        <li className="nav-item">
          <button className={`nav-link ${activeTab === 'log' ? 'active' : ''}`} onClick={() => setActiveTab('log')}>
            <i className="feather-activity me-2"></i>{t('adminNotifications.tabs.log')}
            {notifications.length > 0 && <span className="badge bg-primary ms-2">{notifications.length}</span>}
          </button>
        </li>
        <li className="nav-item">
          <button className={`nav-link ${activeTab === 'deliveries' ? 'active' : ''}`} onClick={() => setActiveTab('deliveries')}>
            <i className="feather-send me-2"></i>{t('adminNotifications.tabs.deliveries')}
          </button>
        </li>
        <li className="nav-item">
          <button className={`nav-link ${activeTab === 'preview' ? 'active' : ''}`} onClick={() => setActiveTab('preview')}>
            <i className="feather-eye me-2"></i>{t('adminNotifications.tabs.preview')}
          </button>
        </li>
      </ul>

      {/* Preview tab: every e-mail template with its fixture (ClickUp 869fkva4j) */}
      {activeTab === 'preview' && (
        <Card>
          <NotificationTemplatePreview />
        </Card>
      )}

      {/* Deliveries tab: what each user was sent (ClickUp 869fkva3u) */}
      {activeTab === 'deliveries' && (
        <Card>
          <NotificationDeliveryLog />
        </Card>
      )}

      {/* Definitions tab */}
      {activeTab === 'definitions' && (
        <Card>
          {/* Filters */}
          <div className="row g-2 mb-4">
            <div className="col-md-3">
              <input type="text" className="form-control form-control-sm" placeholder={t('adminNotifications.filters.search')} value={filterSearch} onChange={e => setFilterSearch(e.target.value)} />
            </div>
            <div className="col-6 col-md-2">
              <select className="form-select form-select-sm" value={filterType} onChange={e => setFilterType(e.target.value)}>
                <option value="">{t('adminNotifications.filters.allTypes')}</option>
                {types.map(ty => <option key={ty} value={ty}>{typeLabel(ty)}</option>)}
              </select>
            </div>
            <div className="col-6 col-md-2">
              <select className="form-select form-select-sm" value={filterRecipient} onChange={e => setFilterRecipient(e.target.value)}>
                <option value="">{t('adminNotifications.filters.allRecipients')}</option>
                {recipients.map(r => <option key={r} value={r}>{recipientLabel(r)}</option>)}
              </select>
            </div>
            <div className="col-4 col-md-1">
              <select className="form-select form-select-sm" value={filterPriority} onChange={e => setFilterPriority(e.target.value)}>
                <option value="">{t('adminNotifications.filters.allPriorities')}</option>
                {priorities.map(p => <option key={p} value={p}>{getPriorityLabel(p)}</option>)}
              </select>
            </div>
            <div className="col-4 col-md-1">
              <select className="form-select form-select-sm" value={filterChannel} onChange={e => setFilterChannel(e.target.value)}>
                <option value="">{t('adminNotifications.filters.allChannels')}</option>
                {channels.map(c => <option key={c} value={c}>{getChannelBadge(c).label}</option>)}
              </select>
            </div>
            <div className="col-4 col-md-1">
              <select className="form-select form-select-sm" value={filterActive} onChange={e => setFilterActive(e.target.value)}>
                <option value="">{t('adminNotifications.filters.allStatus')}</option>
                <option value="true">{t('adminNotifications.filters.active')}</option>
                <option value="false">{t('adminNotifications.filters.inactive')}</option>
              </select>
            </div>
            <div className="col-auto">
              <button className="btn btn-sm btn-outline-secondary" onClick={clearFilters}>
                <i className="feather-x me-1"></i>{t('adminNotifications.filters.clear')}
              </button>
            </div>
            <div className="col-auto">
              <button className="btn btn-sm btn-primary" onClick={() => openDefEditor(null)}>
                <i className="feather-plus me-1"></i>{t('adminNotifications.defEditor.createButton')}
              </button>
            </div>
          </div>

          <div className="text-muted small mb-2">
            {`${filteredDefinitions.length} / ${definitions.length} ${t('adminNotifications.stats.totalDefinitions').toLowerCase()}`}
          </div>

          {/* Table */}
          <div className="table-responsive">
            <table className="table table-hover table-sm">
              <thead>
                <tr>
                  <th>ID</th>
                  <th>{t('adminNotifications.columns.type')}</th>
                  <th>{t('adminNotifications.columns.title')}</th>
                  <th>{t('adminNotifications.columns.recipients')}</th>
                  <th>{t('adminNotifications.columns.channels')}</th>
                  <th>{t('adminNotifications.columns.trigger')}</th>
                  <th>{t('adminNotifications.columns.priority')}</th>
                  <th>{t('adminNotifications.columns.status')}</th>
                </tr>
              </thead>
              <tbody>
                {filteredDefinitions.map(def => (
                  <tr key={def.id} className={!def.active ? 'table-light text-muted' : ''} style={{ cursor: 'pointer' }} onClick={() => openDefEditor(def)}>
                    <td><code className="small">{def.id}</code></td>
                    <td>
                      <span className="badge bg-light text-dark border">
                        <i className={`feather-${def.metadata.icon} me-1`}></i>
                        {typeLabel(def.type)}
                      </span>
                    </td>
                    <td>
                      <div className="fw-semibold">{def.title}</div>
                      <small className="text-muted text-truncate d-block" style={{ maxWidth: '300px' }}>{def.bodyTemplate}</small>
                    </td>
                    <td>
                      <div className="d-flex flex-wrap gap-1">
                        {def.recipients.map(r => (
                          <span key={r} className="badge bg-light text-dark border">{recipientLabel(r)}</span>
                        ))}
                      </div>
                    </td>
                    <td>
                      <div className="d-flex flex-wrap gap-1">
                        {def.channels.map(c => {
                          const ch = getChannelBadge(c);
                          return <span key={c} className={`badge ${ch.color}`}>{ch.label}</span>;
                        })}
                      </div>
                    </td>
                    <td><small>{getTriggerLabel(def.trigger)}</small></td>
                    <td><span className={`badge ${getPriorityBadge(def.priority)}`}>{getPriorityLabel(def.priority)}</span></td>
                    <td>
                      {def.settingsEditable === false ? (
                        <span className="badge bg-secondary text-wrap">{t('adminNotifications.unavailable')}</span>
                      ) : def.active ? (
                        <span className="badge bg-success">{t('adminNotifications.active')}</span>
                      ) : (
                        <span className="badge bg-secondary">{t('adminNotifications.inactive')}</span>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </Card>
      )}

      {/* Log tab */}
      {activeTab === 'log' && (
        <Card>
          {/* Log filters */}
          <div className="row g-2 mb-4">
            <div className="col-md-4">
              <input type="text" className="form-control form-control-sm" placeholder={t('adminNotifications.filters.search')} value={logFilterSearch} onChange={e => setLogFilterSearch(e.target.value)} />
            </div>
            <div className="col-4 col-md-2">
              <select className="form-select form-select-sm" value={logFilterType} onChange={e => setLogFilterType(e.target.value)}>
                <option value="">{t('adminNotifications.filters.allTypes')}</option>
                {types.map(ty => <option key={ty} value={ty}>{typeLabel(ty)}</option>)}
              </select>
            </div>
            <div className="col-4 col-md-2">
              <select className="form-select form-select-sm" value={logFilterPriority} onChange={e => setLogFilterPriority(e.target.value)}>
                <option value="">{t('adminNotifications.filters.allPriorities')}</option>
                {priorities.map(p => <option key={p} value={p}>{getPriorityLabel(p)}</option>)}
              </select>
            </div>
            <div className="col-4 col-md-2">
              <select className="form-select form-select-sm" value={logFilterRead} onChange={e => setLogFilterRead(e.target.value)}>
                <option value="">{t('adminNotifications.filters.allReadStatus')}</option>
                <option value="false">{t('adminNotifications.filters.unread')}</option>
                <option value="true">{t('adminNotifications.filters.read')}</option>
              </select>
            </div>
            <div className="col-4 col-md-2">
              <select className="form-select form-select-sm" value={logFilterRecipient} onChange={e => setLogFilterRecipient(e.target.value)}>
                <option value="">{t('adminNotifications.filters.allRecipients')}</option>
                <option value="Admin">{t('adminNotifications.recipientAdmin')}</option>
                <option value="company">{t('adminNotifications.recipientForetag')}</option>
                <option value="person">{t('adminNotifications.recipientKund')}</option>
                <option value="lawyer">{t('adminNotifications.recipientJurist')}</option>
                <option value="inspector">{t('adminNotifications.recipientBesiktningsman')}</option>
              </select>
            </div>
            <div className="col-auto">
              <button className="btn btn-sm btn-outline-secondary" onClick={clearLogFilters}>
                <i className="feather-x me-1"></i>{t('adminNotifications.filters.clear')}
              </button>
            </div>
          </div>

          {filteredNotifications.length === 0 ? (
            <div className="text-center py-5 text-muted">
              <i className="feather-inbox fs-1 d-block mb-3"></i>
              <p>{t('adminNotifications.noNotifications')}</p>
            </div>
          ) : (
            <div className="table-responsive">
              <table className="table table-hover table-sm">
                <thead>
                  <tr>
                    <th>{t('adminNotifications.columns.date')}</th>
                    <th>{t('adminNotifications.columns.type')}</th>
                    <th>{t('adminNotifications.columns.title')}</th>
                    <th>{t('adminNotifications.columns.recipient')}</th>
                    <th>{t('adminNotifications.columns.channels')}</th>
                    <th>{t('adminNotifications.columns.priority')}</th>
                    <th>{t('adminNotifications.columns.readStatus')}</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredNotifications.slice(0, 100).map(n => (
                    <tr key={n.id} className={!n.read ? 'fw-semibold' : ''} style={{ cursor: 'pointer' }} onClick={() => setSelectedNotif(n)}>
                      <td><small>{new Date(n.createdAt).toLocaleString('sv-SE', { year: 'numeric', month: '2-digit', day: '2-digit', hour: '2-digit', minute: '2-digit' })}</small></td>
                      <td><span className="badge bg-light text-dark border">{typeLabel(n.type)}</span></td>
                      <td>
                        <div>{n.title}</div>
                        <small className="text-muted text-truncate d-block" style={{ maxWidth: '350px' }}>{n.body}</small>
                      </td>
                      <td><span className="badge bg-light text-dark border">{n.recipientType}</span></td>
                      <td>
                        <div className="d-flex flex-wrap gap-1">
                          {n.channels.map(c => {
                            const ch = getChannelBadge(c);
                            return <span key={c} className={`badge ${ch.color}`} style={{ fontSize: '0.7em' }}>{ch.label}</span>;
                          })}
                        </div>
                      </td>
                      <td><span className={`badge ${getPriorityBadge(n.priority)}`} style={{ fontSize: '0.7em' }}>{getPriorityLabel(n.priority)}</span></td>
                      <td>
                        {n.read ? (
                          <span className="text-muted small"><i className="feather-check me-1"></i>{t('adminNotifications.read')}</span>
                        ) : (
                          <span className="text-primary small"><i className="feather-circle me-1"></i>{t('adminNotifications.unread')}</span>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
              {filteredNotifications.length > 100 && (
                <p className="text-muted small text-center">{`100 / ${filteredNotifications.length}`}</p>
              )}
            </div>
          )}
        </Card>
      )}

      {/* Definition Editor Modal */}
      {(editDef || isCreatingDef) && (
        <div className="modal d-block bg-dark bg-opacity-50 overflow-auto" role="dialog" aria-modal="true" aria-label={t('adminNotifications.defEditor.editTitle')} onClick={closeDefEditor}>
          <div className="modal-dialog modal-lg modal-dialog-centered" onClick={e => e.stopPropagation()}>
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">
                  <i className={`feather-${isCreatingDef ? 'plus-circle' : 'edit'} me-2`}></i>
                  {isCreatingDef ? t('adminNotifications.defEditor.createTitle') : t('adminNotifications.defEditor.editTitle')}
                </h5>
                <button type="button" className="btn-close" aria-label={t('common.close')} onClick={closeDefEditor}></button>
              </div>
              <div className="modal-body">
                {defMessage && (
                  <div className={`alert alert-${defMessage.type === 'success' ? 'success' : 'danger'} mb-3`}>{defMessage.text}</div>
                )}

                {settingsUnavailable && (
                  <div className="alert alert-secondary" role="status">
                    <strong>{t('adminNotifications.unavailable')}</strong>
                    <p className="mb-0">{t(editDef?.unavailableReason === 'legacy_only'
                      ? 'adminNotifications.unavailableLegacyHelp'
                      : 'adminNotifications.unavailableImplementationHelp')}</p>
                  </div>
                )}
                <fieldset disabled={settingsUnavailable}>
                <div className="row g-3">
                  <div className="col-md-6">
                    <label className="form-label fw-semibold">{t('adminNotifications.defEditor.eventKey')} *</label>
                    <input type="text" className="form-control" disabled={!isCreatingDef} value={defForm.eventKey} onChange={e => setDefForm(p => ({ ...p, eventKey: e.target.value }))} placeholder="contract.signed_by_both" />
                  </div>
                  <div className="col-md-6">
                    <label className="form-label fw-semibold">{t('adminNotifications.columns.type')} *</label>
                    <select className="form-select" value={defForm.type} onChange={e => setDefForm(p => ({ ...p, type: e.target.value }))}>
                      {allTypes.map(ty => <option key={ty} value={ty}>{typeLabel(ty)}</option>)}
                    </select>
                  </div>
                  <div className="col-12">
                    <label className="form-label fw-semibold">{t('adminNotifications.defEditor.titleField')} *</label>
                    <input type="text" className="form-control" value={defForm.title} onChange={e => setDefForm(p => ({ ...p, title: e.target.value }))} />
                  </div>
                  <div className="col-12">
                    <label className="form-label fw-semibold">{t('adminNotifications.defEditor.bodyTemplate')}</label>
                    <textarea className="form-control" rows={3} value={defForm.bodyTemplate} onChange={e => setDefForm(p => ({ ...p, bodyTemplate: e.target.value }))} placeholder="Hej {{name}}, kontrakt #{{contractNumber}}..." />
                    <small className="text-muted">{t('adminNotifications.defEditor.templateHelp')}</small>
                  </div>

                  <div className="col-md-6">
                    <label className="form-label fw-semibold">{t('adminNotifications.columns.recipients')}</label>
                    <div className="d-flex flex-wrap gap-2">
                      {allRecipients.map(r => (
                        <button key={r} type="button"
                          className={`btn btn-sm ${defForm.recipients.includes(r) ? 'btn-primary' : 'btn-outline-secondary'}`}
                          onClick={() => setDefForm(p => ({ ...p, recipients: toggleArrayItem(p.recipients, r) }))}
                        >{recipientLabel(r)}</button>
                      ))}
                    </div>
                  </div>
                  <div className="col-md-6">
                    <label className="form-label fw-semibold">{t('adminNotifications.columns.channels')}</label>
                    <div className="d-flex flex-wrap gap-2">
                      {allChannels.map(c => (
                        <button key={c} type="button"
                          disabled={settingsUnavailable || requiredChannels.includes(c)}
                          aria-pressed={defForm.channels.includes(c)}
                          className={`btn ${requiredChannels.includes(c) || (settingsUnavailable && defForm.channels.includes(c)) ? 'btn-secondary' : defForm.channels.includes(c) ? 'btn-primary' : 'btn-outline-secondary'}`}
                          onClick={() => setDefForm(p => ({ ...p, channels: toggleArrayItem(p.channels, c) }))}
                        >{getChannelBadge(c).label}</button>
                      ))}
                    </div>
                    {requiredChannels.includes('email') && <p className="small text-muted mt-2 mb-0">{t(categoryLocked ? 'adminNotifications.lockedCategoryEmailHelp' : 'adminNotifications.requiredEmailHelp')}</p>}
                    <p className="small text-muted mt-2 mb-0">{t('adminNotifications.smsDeliveryHelp')}</p>
                  </div>

                  <div className="col-md-4">
                    <label className="form-label fw-semibold">{t('adminNotifications.defEditor.triggerType')}</label>
                    <select className="form-select" value={defForm.triggerType} onChange={e => setDefForm(p => ({ ...p, triggerType: e.target.value }))}>
                      <option value="event">Event</option>
                      <option value="scheduled">{t('adminNotifications.triggerTypeScheduled')}</option>
                      <option value="conditional">{t('adminNotifications.triggerTypeConditional')}</option>
                    </select>
                  </div>
                  <div className="col-md-4">
                    <label className="form-label fw-semibold">{t('adminNotifications.defEditor.triggerEvent')}</label>
                    <input type="text" className="form-control" value={defForm.triggerEvent} onChange={e => setDefForm(p => ({ ...p, triggerEvent: e.target.value }))} placeholder="contract.created" />
                  </div>
                  <div className="col-md-4">
                    {defForm.triggerType === 'scheduled' ? (
                      <>
                        <label className="form-label fw-semibold">{t('adminNotifications.defEditor.offsetDays')}</label>
                        <input type="number" className="form-control" value={defForm.triggerOffsetDays} onChange={e => setDefForm(p => ({ ...p, triggerOffsetDays: e.target.value }))} />
                      </>
                    ) : (
                      <>
                        <label className="form-label fw-semibold">{t('adminNotifications.defEditor.condition')}</label>
                        <input type="text" className="form-control" value={defForm.triggerCondition} onChange={e => setDefForm(p => ({ ...p, triggerCondition: e.target.value }))} />
                      </>
                    )}
                  </div>

                  <div className="col-md-4">
                    <label className="form-label fw-semibold">{t('adminNotifications.columns.priority')}</label>
                    <select className="form-select" value={defForm.priority} onChange={e => setDefForm(p => ({ ...p, priority: e.target.value }))}>
                      {allPriorities.map(p => <option key={p} value={p}>{getPriorityLabel(p)}</option>)}
                    </select>
                  </div>
                  <div className="col-md-4">
                    <label className="form-label fw-semibold">{t('adminNotifications.defEditor.icon')}</label>
                    <div className="input-group">
                      <span className="input-group-text"><i className={`feather-${defForm.icon}`}></i></span>
                      <input type="text" className="form-control" value={defForm.icon} onChange={e => setDefForm(p => ({ ...p, icon: e.target.value }))} placeholder="bell" />
                    </div>
                  </div>
                  <div className="col-md-4">
                    <label className="form-label fw-semibold">{t('adminNotifications.defEditor.color')}</label>
                    <select className="form-select" value={defForm.color} onChange={e => setDefForm(p => ({ ...p, color: e.target.value }))}>
                      {allColors.map(c => <option key={c} value={c}>{c}</option>)}
                    </select>
                  </div>

                  <div className="col-md-8">
                    <label className="form-label fw-semibold">{t('adminNotifications.defEditor.actionUrl')}</label>
                    <input type="text" className="form-control" value={defForm.actionUrl} onChange={e => setDefForm(p => ({ ...p, actionUrl: e.target.value }))} placeholder="/projects/{{projectId}}" />
                  </div>
                  <div className="col-md-4">
                    <label className="form-label fw-semibold">{t('adminNotifications.columns.status')}</label>
                    <div className="form-check form-switch mt-2">
                      <input className="form-check-input" type="checkbox" disabled={requiredChannels.length > 0 && !categoryLocked} aria-label={t('adminNotifications.columns.status')} checked={defForm.active} onChange={e => setDefForm(p => ({ ...p, active: e.target.checked }))} />
                      <label className="form-check-label">{defForm.active ? t('adminNotifications.active') : t('adminNotifications.inactive')}</label>
                    </div>
                  </div>
                </div>
                </fieldset>
              </div>
              <div className="modal-footer">
                <button className="btn btn-secondary" onClick={closeDefEditor}>{t('common.cancel')}</button>
                <button className="btn btn-primary" onClick={handleSaveDef} disabled={isSavingDef || settingsUnavailable}>
                  {isSavingDef ? (
                    <><span className="spinner-border spinner-border-sm me-1"></span>{t('common.saving')}</>
                  ) : (
                    <><i className="feather-save me-1"></i>{t('common.save')}</>
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Notification Detail Modal */}
      {selectedNotif && (
        <div className="modal d-block" style={{ backgroundColor: 'rgba(0,0,0,0.5)', zIndex: 1050 }} onClick={() => setSelectedNotif(null)}>
          <div className="modal-dialog modal-dialog-centered" onClick={e => e.stopPropagation()}>
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title d-flex align-items-center gap-2">
                  <span className={`badge bg-${selectedNotif.metadata?.color || 'secondary'} rounded-circle p-2`}>
                    <i className={`feather-${selectedNotif.metadata?.icon || 'bell'}`}></i>
                  </span>
                  {selectedNotif.title}
                </h5>
                <button type="button" className="btn-close" onClick={() => setSelectedNotif(null)}></button>
              </div>
              <div className="modal-body">
                <div className="mb-3 p-3 bg-light rounded">
                  <p className="mb-0">{selectedNotif.body}</p>
                </div>

                <table className="table table-sm table-borderless mb-0">
                  <tbody>
                    <tr>
                      <td className="text-muted fw-semibold" style={{ width: '140px' }}>{t('adminNotifications.detail.id')}</td>
                      <td><code>{selectedNotif.id}</code></td>
                    </tr>
                    <tr>
                      <td className="text-muted fw-semibold">{t('adminNotifications.detail.definitionId')}</td>
                      <td><code>{selectedNotif.definitionId}</code></td>
                    </tr>
                    <tr>
                      <td className="text-muted fw-semibold">{t('adminNotifications.detail.eventKey')}</td>
                      <td><code>{selectedNotif.eventKey}</code></td>
                    </tr>
                    <tr>
                      <td className="text-muted fw-semibold">{t('adminNotifications.columns.type')}</td>
                      <td><span className="badge bg-light text-dark border">{typeLabel(selectedNotif.type)}</span></td>
                    </tr>
                    <tr>
                      <td className="text-muted fw-semibold">{t('adminNotifications.columns.recipient')}</td>
                      <td>
                        <span className="badge bg-light text-dark border">{selectedNotif.recipientType}</span>
                        <small className="text-muted ms-2">{selectedNotif.recipientUserId}</small>
                      </td>
                    </tr>
                    <tr>
                      <td className="text-muted fw-semibold">{t('adminNotifications.columns.channels')}</td>
                      <td>
                        <div className="d-flex gap-1">
                          {selectedNotif.channels.map(c => {
                            const ch = getChannelBadge(c);
                            return <span key={c} className={`badge ${ch.color}`}>{ch.label}</span>;
                          })}
                        </div>
                      </td>
                    </tr>
                    <tr>
                      <td className="text-muted fw-semibold">{t('adminNotifications.columns.priority')}</td>
                      <td><span className={`badge ${getPriorityBadge(selectedNotif.priority)}`}>{getPriorityLabel(selectedNotif.priority)}</span></td>
                    </tr>
                    <tr>
                      <td className="text-muted fw-semibold">{t('adminNotifications.detail.created')}</td>
                      <td>{new Date(selectedNotif.createdAt).toLocaleString('sv-SE', { year: 'numeric', month: '2-digit', day: '2-digit', hour: '2-digit', minute: '2-digit', second: '2-digit' })}</td>
                    </tr>
                    <tr>
                      <td className="text-muted fw-semibold">{t('adminNotifications.columns.readStatus')}</td>
                      <td>
                        {selectedNotif.read ? (
                          <span className="text-success"><i className="feather-check-circle me-1"></i>{t('adminNotifications.read')} — {selectedNotif.readAt ? new Date(selectedNotif.readAt).toLocaleString('sv-SE', { year: 'numeric', month: '2-digit', day: '2-digit', hour: '2-digit', minute: '2-digit' }) : ''}</span>
                        ) : (
                          <span className="text-primary"><i className="feather-circle me-1"></i>{t('adminNotifications.unread')}</span>
                        )}
                      </td>
                    </tr>
                    {selectedNotif.metadata?.actionUrl && (
                      <tr>
                        <td className="text-muted fw-semibold">{t('adminNotifications.detail.actionUrl')}</td>
                        <td><code>{selectedNotif.metadata.actionUrl}</code></td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
              <div className="modal-footer">
                <button className="btn btn-secondary" onClick={() => setSelectedNotif(null)}>{t('common.close')}</button>
              </div>
            </div>
          </div>
        </div>
      )}
    </DashboardLayout>
  );
}
